import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from "@angular/core";
import { LayoutWidget } from "../../layouts/se.layout.widget.abstract";
import { Layouts } from "../../layouts/se.layouts";
import { SeOrgDetailsStore } from "../se.org.details.store";
import { S25Util } from "../../../../util/s25-util";
import { CustomAttributes } from "../../../../pojo/CustomAttributes";
import { S25IconComponent } from "../../../s25-icon/s25.icon.component";
import { S25Icons } from "../../../s25-icon/s25.icon.const";

@Component({
    standalone: true,
    selector: "s25-ng-se-org-social-links-widget",
    template: `
        @if (socialLinks().length) {
            <nav [attr.aria-label]="'Organization social links'">
                <div class="social-links">
                    @for (link of socialLinks(); track link.attributeId) {
                        <a
                            [href]="link.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            [attr.aria-label]="link.attributeName"
                            [title]="link.attributeName"
                            class="social-link"
                        >
                            <s25-ng-icon [type]="link.icon" [size]="35" [ariaLabel]="false" />
                        </a>
                    }
                </div>
            </nav>
        }
    `,
    styles: `
        .social-links {
            display: flex;
            gap: 0.75rem;
            flex-wrap: wrap;
        }

        .social-link {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 24px;
            min-height: 24px;
            color: var(--cn-blue);
            border-radius: 4px;
            padding: 4px;
            transition: color var(--transition-short) ease;
        }

        .social-link:hover {
            color: var(--color--link-text-hover);
        }
    `,
    imports: [S25IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
})
export class SeOrgSocialLinksWidgetComponent extends LayoutWidget<typeof SeOrgSocialLinksWidgetComponent> {
    static readonly META = {
        name: "Social Links",
        info: "Displays icon links for the selected organization URL custom attributes.",
        settings: [
            {
                id: "attributeIds",
                label: "Social Link Attributes",
                type: "field" as const,
                fieldType: "attribute-picker" as const,
                options: { attributeTypes: [CustomAttributes.type.URL] },
            },
        ],
        defaultSettings: {
            attributeIds: "",
        },
    } as const satisfies Layouts.WidgetStatic;

    private static readonly iconByDomain: Record<string, S25Icons.Name> = {
        "instagram.com": S25Icons.names.instagram,
        "facebook.com": S25Icons.names.facebook,
        "fb.com": S25Icons.names.facebook,
        "linkedin.com": S25Icons.names.linkedin,
        "twitter.com": S25Icons.names.twitter,
        "x.com": S25Icons.names.twitter,
        "youtube.com": S25Icons.names.youtube,
        "youtu.be": S25Icons.names.youtube,
        "tiktok.com": S25Icons.names.tiktok,
        "discord.com": S25Icons.names.discord,
        "discord.gg": S25Icons.names.discord,
    };

    /** Parses an attribute value as an absolute http(s) URL, assuming https when no scheme is given. */
    static toSafeUrl(value: string): URL | undefined {
        const trimmedValue = value.trim();
        const withScheme = /^[a-z][a-z\d+.-]*:/i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
        let url: URL;
        try {
            url = new URL(withScheme);
        } catch {
            return undefined;
        }
        return url.protocol === "http:" || url.protocol === "https:" ? url : undefined;
    }

    static iconForUrl(value: string): S25Icons.Name {
        const hostname = SeOrgSocialLinksWidgetComponent.toSafeUrl(value)?.hostname.toLowerCase();
        if (!hostname) return S25Icons.names.globe;
        const matchedDomain = Object.keys(SeOrgSocialLinksWidgetComponent.iconByDomain).find(
            (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
        );
        return matchedDomain ? SeOrgSocialLinksWidgetComponent.iconByDomain[matchedDomain] : S25Icons.names.globe;
    }

    protected readonly orgStore = inject(SeOrgDetailsStore);

    private readonly configuredIds = computed(() => {
        const raw = this.settingValues()["attributeIds"];
        if (!raw) return new Set<number>();
        return new Set(
            raw
                .split(",")
                .map((part) => S25Util.parseInt(part.trim()))
                .filter((id): id is number => id !== null && Number.isFinite(id)),
        );
    });

    protected readonly socialLinks = computed(() => {
        const ids = this.configuredIds();
        if (!ids.size) return [];
        return this.orgStore
            .attributes()
            .filter((attribute) => ids.has(attribute.attributeId) && !!attribute.value?.trim())
            .flatMap((attribute) => {
                const url = SeOrgSocialLinksWidgetComponent.toSafeUrl(attribute.value);
                if (!url) return [];
                return [
                    {
                        attributeId: attribute.attributeId,
                        attributeName: attribute.attributeName,
                        href: url.href,
                        icon: SeOrgSocialLinksWidgetComponent.iconForUrl(url.href),
                    },
                ];
            });
    });
}
